import { StateContext } from "@client/pages/round/Round";
import { formatMoney } from "@client/utils/formatMoney";
import {
  getEvaluableOrPlaceholder,
  getEvaluableRequestBase,
  getEvaluableRequestMax,
  getEvaluableRequestMin,
} from "@engine/round/accessors";
import { Funder, Recommender } from "@engine/round/aggregate";
import { useContext } from "react";
import { EvaluableCard, PlaceholderCard } from "../EvaluableCard/EvaluableCard";
import { FrontendEvaluation } from "../useEvaluatorEvaluationsCache";
import { AllocationChartItemData } from "./AllocationChart";

export type AllocationChartItemProps = {
  isSmol: boolean;
  height: number;
  fontSize: number;
} & AllocationChartItemData;

export function AllocationChartItem({
  evaluableId,
  allocation,
  height,
  fontSize,
  isSmol,
  evaluator,
  evaluation,
}: AllocationChartItemProps & {
  evaluator?: Recommender | Funder;
  evaluation?: FrontendEvaluation;
}) {
  const { round } = useContext(StateContext);
  const evaluable = getEvaluableOrPlaceholder(round, evaluableId);

  const evaluableMin = getEvaluableRequestMin(round, evaluableId);
  const evaluableMax = getEvaluableRequestMax(round, evaluableId);
  const evaluableBase = getEvaluableRequestBase(round, evaluableId);

  const belowMinimum = evaluableMin
    ? Math.round(allocation / 1000) < Math.round(evaluableMin / 1000)
    : false;

  const aboveMaximum = evaluableMax
    ? evaluableMax > 0 &&
      Math.round(allocation / 1000) >= Math.round(evaluableMax / 1000)
    : false;

  const aboveBase = evaluableBase
    ? Math.floor(allocation / 1000) >= Math.ceil(evaluableBase / 1000)
    : false;

  const marginLeft = isSmol ? "20px" : "";

  return (
    <EvaluableCard
      evaluable={evaluable}
      evaluator={evaluator}
      evaluation={evaluation}
      style={{
        height,
        marginLeft,
        fontSize,
      }}
    >
      <span>{formatMoney(allocation)}</span>
      {belowMinimum && ` (< min ${formatMoney(evaluableMin!)})`}
      {aboveBase && evaluableBase && evaluableMax && !aboveMaximum && (
        <span style={{ opacity: 0.5 }}>
          {` (≥ base @ ${formatMoney(evaluableBase)})`}
        </span>
      )}
      {aboveMaximum && (
        <span style={{ opacity: 0.5 }}>
          {` (≥ max ${formatMoney(evaluableMax!)})`}
        </span>
      )}
    </EvaluableCard>
  );
}

export function AllocationChartItemOther({
  allocation,
  height,
  fontSize,
}: {
  height: number;
  fontSize: number;
  allocation: number;
}) {
  return (
    <PlaceholderCard
      label="Other"
      style={{
        height,
        fontSize,
      }}
    >
      {formatMoney(allocation)}
    </PlaceholderCard>
  );
}
